import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import api from "@/lib/api";

function fmt(d) {
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (err) {
      // Transient: poll runs every 20s; log only on dev so prod console stays clean
      if (process.env.NODE_ENV !== "production") console.warn("notifications/load", err?.message || err);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      load();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("notifications/markRead", err?.message || err);
    }
  };

  const markAll = async () => {
    try {
      await api.post(`/notifications/read-all`);
      load();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("notifications/markAll", err?.message || err);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-slate-100"
          data-testid="notifications-bell"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5 text-slate-700" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold"
              data-testid="notifications-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <p className="text-sm font-semibold">Notifications</p>
          <Button
            variant="ghost" size="sm"
            onClick={markAll}
            disabled={unread === 0}
            data-testid="notifications-mark-all"
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all
          </Button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-slate-500 px-4 py-6 text-center">No notifications yet.</p>
          ) : (
            items.map((n) => (
              <Link
                key={n.id}
                to={n.link || "/dashboard"}
                onClick={() => { setOpen(false); markRead(n); }}
                className={`flex gap-2 px-3 py-2 border-b last:border-b-0 hover:bg-slate-50 ${n.read ? "opacity-60" : "bg-sky-50/40"}`}
                data-testid={`notif-${n.id}`}
              >
                <div className="mt-1">
                  {n.read ? <Check className="h-4 w-4 text-slate-400" /> : <span className="block h-2 w-2 rounded-full bg-[#4FA3D1] mt-1" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 break-words">{n.message}</p>
                  <p className="text-[11px] text-slate-500">{fmt(n.created_at)}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
